import {
  MoreHorizontalIcon,
  Trash2Icon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import type { MerchantProcessor } from "@/types/admin-merchant-processor"
import type { ProcessorCertificateStatus } from "@/types/admin-processor-config"

import { isUnused, shouldShowExpiry } from "../utils"

const prefix = "pages.adminMerchantProcessors"

// Cor da faixa lateral por saúde do certificado. O status vira ESTRUTURA: lê-se
// sem precisar ler. Mas cor NUNCA é o único portador — o rótulo do status vai
// junto, visível quando pede ação e em `sr-only` no resto.
const EDGE_BY_STATUS: Record<
  ProcessorCertificateStatus,
  string
> = {
  EXPIRED: "bg-red-600",
  EXPIRING: "bg-amber-500",
  VALID: "bg-emerald-500",
  NOT_DEFINED: "bg-gray-400",
  NOT_APPLICABLE: "bg-gray-300",
}

// Status que pedem ação aparecem escritos; os demais ficam só para leitor de
// tela, senão a lista volta a ter um badge por linha dizendo "está tudo bem".
const NEEDS_ATTENTION = new Set<ProcessorCertificateStatus>(
  ["EXPIRED", "EXPIRING", "NOT_DEFINED"]
)

type MerchantProcessorRowProps = {
  processor: MerchantProcessor
  /** Nome do white label pelo id; sem ele, sobra o uuid — que não diz nada. */
  whiteLabelName: (id: string) => string
  onOpen: (processor: MerchantProcessor) => void
  onDelete: (processor: MerchantProcessor) => void
}

/** Quantos white labels cabem na linha antes de virar "+N". */
const VISIBLE_WHITE_LABELS = 2

export const MerchantProcessorRow = ({
  processor,
  whiteLabelName,
  onOpen,
  onDelete,
}: MerchantProcessorRowProps) => {
  const { t } = useTranslation()

  const status: ProcessorCertificateStatus =
    processor.certificate?.status ?? "NOT_APPLICABLE"
  const days = processor.certificate?.daysToExpire
  const unused = isUnused(processor)

  const whiteLabels = processor.linkedWhiteLabelIds ?? []
  const shownWhiteLabels = whiteLabels
    .slice(0, VISIBLE_WHITE_LABELS)
    .map(whiteLabelName)
  const hiddenCount =
    whiteLabels.length - shownWhiteLabels.length

  return (
    <li className="relative overflow-hidden rounded-lg border bg-card transition-colors hover:bg-muted/40">
      {/* Faixa de saúde: 3px que substituem um badge de status por linha. */}
      <span
        aria-hidden="true"
        className={cn(
          "absolute inset-y-0 left-0 w-[3px]",
          EDGE_BY_STATUS[status] ?? "bg-zinc-200"
        )}
      />

      <div className="flex items-center gap-3 py-3 pl-4 pr-2">
        {/* A linha inteira abre o detalhe: alvo grande, e o nome continua
            sendo o rótulo acessível da ação. */}
        <button
          type="button"
          // O `overflow-hidden` do <li> cortaria um ring externo.
          className="min-w-0 flex-1 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          onClick={() => onOpen(processor)}
        >
          <span className="block truncate font-semibold">
            {processor.name ?? processor.gateway}
          </span>
          <span
            title={
              hiddenCount > 0
                ? whiteLabels.map(whiteLabelName).join(", ")
                : undefined
            }
            className="mt-0.5 block truncate text-xs text-muted-foreground"
          >
            {whiteLabels.length === 0
              ? t(`${prefix}.noLinkedConfigs`)
              : [
                  shownWhiteLabels.join(", ") +
                    (hiddenCount > 0
                      ? ` +${hiddenCount}`
                      : ""),
                  t(`${prefix}.linkedCount`, {
                    count: processor.linkedConfigsCount,
                  }),
                ].join(" · ")}
          </span>
        </button>

        {/* Verde da marca: o azul era o único chip fora da paleta da Pay2B nesta tela, e
            competia com o estado das credenciais em vez de acompanhar o resto. */}
        <div className="hidden shrink-0 gap-1.5 @xl:flex">
          {processor.methods.map((method) => (
            <Badge
              key={method}
              variant="outline"
              className="border-emerald-200 bg-emerald-50 font-normal text-emerald-700"
            >
              {t(
                `pages.adminProcessorConfig.methods.${method}`,
                {
                  defaultValue: method,
                }
              )}
            </Badge>
          ))}
        </div>

        {/* O que a faixa colorida diz, dito também em palavras. Com prazo
            quando ele existe; sem ele, o rótulo do status sozinho — senão uma
            credencial SEM certificado ficaria muda, apesar de a lista já a
            ordenar como "precisa de ação". */}
        <span
          className={cn(
            "shrink-0 text-xs font-medium tabular-nums",
            !NEEDS_ATTENTION.has(status) && "sr-only",
            status === "EXPIRED" && "text-red-700",
            status === "EXPIRING" && "text-amber-700",
            status === "NOT_DEFINED" && "text-gray-600"
          )}
        >
          {shouldShowExpiry(processor) &&
          typeof days === "number"
            ? days < 0
              ? t(`${prefix}.certificateExpired`)
              : t(`${prefix}.certificateExpiresIn`, {
                  days,
                })
            : t(`${prefix}.certificateStatus.${status}`)}
        </span>

        {unused ? (
          <>
            <Badge
              variant="outline"
              className="shrink-0 border-gray-200 bg-gray-50 font-normal text-gray-700"
            >
              {t(`${prefix}.unused`)}
            </Badge>
            {/* Só a credencial livre mostra a lixeira direto: nas outras ela
                estaria bloqueada de qualquer forma. */}
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 text-muted-foreground hover:text-destructive"
              aria-label={t(`${prefix}.actions.delete`)}
              onClick={() => onDelete(processor)}
            >
              <Trash2Icon className="size-4" />
            </Button>
          </>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                aria-label={t(`${prefix}.actions.more`, {
                  name: processor.name ?? processor.gateway,
                })}
              >
                <MoreHorizontalIcon className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => onOpen(processor)}
              >
                {t(`${prefix}.actions.edit`)}
              </DropdownMenuItem>
              {/* Label, e não item desabilitado: o Radix não dá foco a item
                  desabilitado, e o motivo do bloqueio ficaria inalcançável por
                  teclado. */}
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {t(`${prefix}.deleteBlocked`)}
              </DropdownMenuLabel>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </li>
  )
}
